/**
 * <docs-resize> -- resizable demo container for brio docs pages.
 *
 * Wrap any demo content; the element gets a draggable right-edge handle,
 * a live width readout, keyboard support (Arrow/Home/End, Shift for
 * larger step), and touch support. Without JS, native CSS
 * `resize: horizontal` remains functional via the tag-selector fallback
 * in src/docs/docs.css.
 *
 * Markup:
 *   <docs-resize>
 *     <!-- whatever you want to test at different widths -->
 *   </docs-resize>
 *
 * Scope: docs-only. This element does not ship in brio.js. Lives in
 * src/docs/ and is registered from src/docs/docs.js.
 */

import { BrioBase } from '../js/brio-base.js';

const STEP_SMALL = 10;
const STEP_LARGE = 50;

const GRIP_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="6" height="18" fill="currentColor" aria-hidden="true">' +
    '<circle cx="1.5" cy="3" r="1.5"/><circle cx="4.5" cy="3" r="1.5"/>' +
    '<circle cx="1.5" cy="9" r="1.5"/><circle cx="4.5" cy="9" r="1.5"/>' +
    '<circle cx="1.5" cy="15" r="1.5"/><circle cx="4.5" cy="15" r="1.5"/>' +
    '</svg>';

class DocsResize extends BrioBase {
    static tag = 'docs-resize';
    static {
        customElements.define(DocsResize.tag, DocsResize);
    }

    #handle;
    #readout;
    #resizeObserver;
    #onPointerDown;
    #onKeydown;

    connectedCallback() {
        this.#handle = this.#createHandle();
        this.#readout = this.#createReadout();
        this.appendChild(this.#handle);
        this.appendChild(this.#readout);

        const limits = this.#readLimits();
        const initial = Math.round(this.offsetWidth);
        this.#handle.setAttribute('aria-valuemin', Math.round(limits.min));
        this.#handle.setAttribute('aria-valuemax', Math.round(limits.max));
        this.#handle.setAttribute('aria-valuenow', initial);
        this.#readout.textContent = initial + 'px';

        this.#onPointerDown = this.#handlePointerDown.bind(this);
        this.#onKeydown = this.#handleKeydown.bind(this);
        this.#handle.addEventListener('pointerdown', this.#onPointerDown);
        this.#handle.addEventListener('keydown', this.#onKeydown);

        // Keep the readout honest if the viewport (or a parent container)
        // resizes and clamps our width. The ResizeObserver fires for any
        // change, including user drags, so we only update display values
        // here; width is set directly in the pointer handler.
        this.#resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const w = Math.round(
                    entry.contentBoxSize?.[0]?.inlineSize ?? entry.target.offsetWidth
                );
                this.#readout.textContent = w + 'px';
                this.#handle.setAttribute('aria-valuenow', w);
            }
        });
        this.#resizeObserver.observe(this);
    }

    disconnectedCallback() {
        if (this.#handle) {
            this.#handle.removeEventListener('pointerdown', this.#onPointerDown);
            this.#handle.removeEventListener('keydown', this.#onKeydown);
            this.#handle.remove();
        }
        if (this.#readout) this.#readout.remove();
        if (this.#resizeObserver) this.#resizeObserver.disconnect();
    }

    #createHandle() {
        const handle = document.createElement('button');
        handle.type = 'button';
        handle.className = 'docs-resize-handle';
        handle.setAttribute('role', 'separator');
        handle.setAttribute('aria-orientation', 'vertical');
        handle.setAttribute('aria-label', 'Resize container');
        handle.innerHTML = GRIP_SVG;
        return handle;
    }

    #createReadout() {
        const readout = document.createElement('output');
        readout.className = 'docs-resize-readout';
        readout.setAttribute('aria-live', 'polite');
        return readout;
    }

    /**
     * Read min/max width limits from computed style. Falls back to the
     * parent's client width when max is unset, so unconstrained resizes
     * still have a ceiling.
     */
    #readLimits() {
        const cs = getComputedStyle(this);
        const min = parsePx(cs.minInlineSize) || parsePx(cs.minWidth) || 0;
        let max = parsePx(cs.maxInlineSize) || parsePx(cs.maxWidth) || 0;
        if (max <= 0 && this.parentElement) {
            max = this.parentElement.clientWidth;
        }
        return { min, max };
    }

    #applyWidth(width, limits) {
        const clamped = Math.round(Math.min(limits.max, Math.max(limits.min, width)));
        this.style.inlineSize = clamped + 'px';
        this.#readout.textContent = clamped + 'px';
        this.#handle.setAttribute('aria-valuenow', clamped);
    }

    #handlePointerDown(e) {
        if (e.button !== 0) return;
        e.preventDefault();
        this.#handle.setPointerCapture(e.pointerId);
        this.classList.add('is-dragging');

        const startX = e.clientX;
        const startWidth = this.offsetWidth;
        const limits = this.#readLimits();
        const rtl = getComputedStyle(this).direction === 'rtl';

        const onMove = (ev) => {
            const delta = rtl ? startX - ev.clientX : ev.clientX - startX;
            this.#applyWidth(startWidth + delta, limits);
        };
        const onUp = () => {
            this.classList.remove('is-dragging');
            this.#handle.removeEventListener('pointermove', onMove);
            this.#handle.removeEventListener('pointerup', onUp);
            this.#handle.removeEventListener('lostpointercapture', onUp);
        };

        this.#handle.addEventListener('pointermove', onMove);
        this.#handle.addEventListener('pointerup', onUp);
        this.#handle.addEventListener('lostpointercapture', onUp);
    }

    #handleKeydown(e) {
        const step = e.shiftKey ? STEP_LARGE : STEP_SMALL;
        const limits = this.#readLimits();
        const current = this.offsetWidth;
        let next;

        switch (e.key) {
            case 'ArrowRight':
                next = current + step;
                break;
            case 'ArrowLeft':
                next = current - step;
                break;
            case 'Home':
                next = limits.min;
                break;
            case 'End':
                next = limits.max;
                break;
            default:
                return;
        }

        e.preventDefault();
        this.#applyWidth(next, limits);
    }
}

function parsePx(computed) {
    if (!computed || computed === 'none') return 0;
    if (computed.indexOf('%') !== -1) return 0;
    const n = parseFloat(computed);
    return Number.isFinite(n) ? n : 0;
}
